import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User } from '@/types/auth'
import { authApi } from '@/api'
import { USER_TYPES } from '@/constants/userRoles'

export interface LogoutOptions {
  redirect?: boolean
}

export const useAuthStore = defineStore('auth', () => {
  // 状态
  const user = ref<User | null>(null)
  const token = ref<string>('')
  const permissions = ref<string[]>([])

  // 计算属性
  const isAuthenticated = computed(() => !!token.value)
  const userType = computed(() => user.value?.userType || 0)
  const isAdmin = computed(() => userType.value === USER_TYPES.PLATFORM_ADMIN)
  const isMerchant = computed(() => userType.value === USER_TYPES.MERCHANT_ADMIN)

  const modulePermissionMap: Record<string, string[]> = {
    module_rules: ['merchant:config:view'],
    module_shop: ['merchant:shop:view'],
    module_employee: ['merchant:employee:view'],
    module_member: ['merchant:member:view'],
    module_points: ['merchant:points:view'],
    module_mall: ['merchant:mall:view'],
    module_sms: ['merchant:sms:view'],
    module_child: ['merchant:child:view'],
    module_subject_document: ['merchant:subject-doc:view'],
    module_service_appointment: ['merchant:service:view'],
    module_outstanding_student: ['merchant:outstanding:view'],
    module_news: ['merchant:news:view'],
    module_recommended_courses: ['merchant:recommended:view'],
    module_home_management: ['merchant:config:view'],
    module_home_carousel: ['merchant:config:view'],
    module_education_booking: ['merchant:service:view'],
    module_lecture_booking: ['merchant:service:view'],
    module_higher_education_booking: ['merchant:service:view'],
    module_forum_management: ['merchant:config:view'],
  }

  const resolvePermissions = (type: number, enabledModuleIds: string[] = []) => {
    const platformPermissions = [
      'platform:view',
      'platform:merchant:view',
      'platform:module:view',
      'platform:user:view',
    ]

    const merchantAdminPermissions = [
      'merchant:view',
      'merchant:config:view',
      'merchant:member:view',
      'merchant:points:view',
      'merchant:level:view',
      'merchant:shop:view',
      'merchant:employee:view',
      'merchant:mall:view',
      'merchant:sms:view',
      'merchant:child:view',
      'merchant:subject-doc:view',
      'merchant:service:view',
      'merchant:outstanding:view',
      'merchant:news:view',
      'merchant:recommended:view',
    ]

    const storeAdminPermissions = [
      'merchant:view',
      'merchant:config:view',
      'merchant:member:view',
      'merchant:points:view',
      'merchant:level:view',
      'merchant:employee:view',
      'merchant:mall:view',
      'merchant:child:view',
      'merchant:service:view',
      'merchant:outstanding:view',
      'merchant:news:view',
      'merchant:recommended:view',
    ]

    const storeStaffPermissions = [
      'merchant:view',
      'merchant:member:view',
      'merchant:points:view',
      'merchant:child:view',
      'merchant:service:view',
      'merchant:outstanding:view',
      'merchant:news:view',
      'merchant:recommended:view',
    ]

    const storeFinancePermissions = [...storeStaffPermissions, 'merchant:points:adjust']
    const storeAdministrativePermissions = [...storeStaffPermissions, 'merchant:mall:view']

    const modulePermissions = enabledModuleIds.flatMap((code) => modulePermissionMap[code] || [])
    if (type === USER_TYPES.PLATFORM_ADMIN) return platformPermissions
    if (type === USER_TYPES.MERCHANT_ADMIN) return [...new Set([...merchantAdminPermissions, ...modulePermissions, 'merchant:points:adjust'])]
    if (type === USER_TYPES.BRANCH_MANAGER) return [...new Set([...storeAdminPermissions, ...modulePermissions, 'merchant:points:adjust'])]
    if (type === USER_TYPES.BRANCH_STAFF) return [...new Set([...storeStaffPermissions, ...modulePermissions])]
    if (type === USER_TYPES.BRANCH_FINANCE) return [...new Set([...storeFinancePermissions, ...modulePermissions])]
    if (type === USER_TYPES.BRANCH_ADMINISTRATIVE) return [...new Set([...storeAdministrativePermissions, ...modulePermissions])]
    return []
  }

  const resolveUserPermissions = (userData: User) => {
    if (Array.isArray(userData.permissionCodes)) {
      const viewModuleCodes = userData.permissionCodes.filter((code) => code.startsWith('module_'))
      const modulePermissions = viewModuleCodes.flatMap((code) => modulePermissionMap[code] || [])
      const basePermissions =
        userData.userType === USER_TYPES.PLATFORM_ADMIN
          ? ['platform:view', 'platform:merchant:view', 'platform:module:view', 'platform:user:view']
          : ['merchant:view']
      return [...new Set([...basePermissions, ...modulePermissions, ...userData.permissionCodes])]
    }
    return [
      ...new Set([
        ...resolvePermissions(userData.userType, userData.enabledModuleIds || []),
        ...(userData.permissionCodes || []),
      ]),
    ]
  }

  // 方法
  const login = async (loginData: { mobile: string; password: string }) => {
    try {
      const response = await authApi.login(loginData)
      
      if (response.success) {
        const { user: userData, accessToken } = response.data
        
        const userPermissions = resolveUserPermissions(userData)
        
        // 设置认证信息
        setAuth(userData, accessToken, userPermissions)
        
        return { success: true }
      } else {
        return { success: false, message: response.message || '登录失败' }
      }
    } catch (error: any) {
      console.error('Login failed:', error)
      return { success: false, message: error.message || '登录失败' }
    }
  }

  const redirectToLogin = () => {
    if (window.location.pathname !== '/login') {
      window.location.href = '/login'
    }
  }

  const logout = async (options: LogoutOptions = {}) => {
    const shouldRedirect = options.redirect !== false
    try {
      // 调用退出登录API
      if (token.value) {
        await authApi.logout()
      }
    } catch (error) {
      console.error('Logout API failed:', error)
    } finally {
      // 清理本地状态
      user.value = null
      token.value = ''
      permissions.value = []
      localStorage.removeItem('auth_token')
      localStorage.removeItem('auth_user')

      if (shouldRedirect) {
        redirectToLogin()
      }
    }
  }

  const setAuth = (userData: User, authToken: string, userPermissions: string[]) => {
    user.value = userData
    token.value = authToken
    permissions.value = userPermissions
    
    // 持久化存储
    localStorage.setItem('auth_token', authToken)
    localStorage.setItem('auth_user', JSON.stringify(userData))
  }

  const refreshProfile = async () => {
    if (!token.value) return
    const response = await authApi.getProfile()
    if (response.success) {
      user.value = response.data
      permissions.value = resolveUserPermissions(response.data)
      localStorage.setItem('auth_user', JSON.stringify(response.data))
    }
  }

  const hasPermissions = (requiredPermissions: string[]): boolean => {
    return requiredPermissions.every(permission => 
      permissions.value.includes(permission)
    )
  }

  const initAuth = async () => {
    const savedToken = localStorage.getItem('auth_token')
    const savedUser = localStorage.getItem('auth_user')
    
    if (savedToken && savedUser) {
      try {
        const userData = JSON.parse(savedUser)
        token.value = savedToken
        user.value = userData
        
        const defaultPermissions = resolveUserPermissions(userData)
        permissions.value = defaultPermissions
        
        // 验证token是否仍然有效，获取最新用户信息
        try {
          const profileResponse = await authApi.getProfile()
          if (profileResponse.success) {
            user.value = profileResponse.data
            permissions.value = resolveUserPermissions(profileResponse.data)
          }
        } catch (error) {
          console.error('Failed to refresh profile:', error)
          // Token可能已过期，清理认证状态
          logout({ redirect: false })
        }
      } catch (error) {
        console.error('Failed to restore auth:', error)
        logout({ redirect: false })
      }
    }
  }

  return {
    // 状态
    user,
    token,
    permissions,
    // 计算属性
    isAuthenticated,
    userType,
    isAdmin,
    isMerchant,
    // 方法
    login,
    logout,
    setAuth,
    hasPermissions,
    initAuth,
    refreshProfile,
  }
})
